
import { Link } from 'react-router-dom';
import Button from '../../../../components/base/Button';
import { useEffect, useState, useRef } from 'react';

function AnimatedCounter({
  end,
  duration,
  suffix = '',
  prefix = '',
  isWarranty = false,
}: {
  end: number;
  duration: number;
  suffix?: string;
  prefix?: string;
  isWarranty?: boolean;
}) {
  const [count, setCount] = useState(0);
  const [showLifetime, setShowLifetime] = useState(false);
  const countRef = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;

          const startTime = Date.now();
          const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const current = Math.floor(easeOutQuart * end);

            setCount(current);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
              if (isWarranty) {
                // small delay before showing the “Lifetime!” text
                setTimeout(() => setShowLifetime(true), 300);
              }
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, isWarranty]);

  return (
    <span
      ref={countRef}
      className="font-bold text-4xl md:text-5xl max-md:text-2xl"
    >
      {prefix}
      {showLifetime ? (
        <span className="gradient-text-hd bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-400 bg-clip-text text-transparent animate-fade-in">
          Lifetime!
        </span>
      ) : (
        <>
          {count.toLocaleString()}
          {suffix}
        </>
      )}
    </span>
  );
}

export default function HeroSection() {
  return (
    <section className="relative min-h-screen max-md:min-h-[70vh] flex items-center justify-center overflow-hidden mobile-compact-hero">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-105"
        style={{
          backgroundImage:
            'url(https://img1.wsimg.com/isteam/ip/9ebfc883-4205-41ee-a091-a5f3a9011f92/blob-0005.png)',
          imageRendering: 'high-quality',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/45"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 max-md:py-12 text-center">
        <div
          className="animate-fade-in-smooth"
          style={{
            transform: 'translateZ(0)',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
        >
          <h1
            className="text-5xl md:text-7xl max-md:text-3xl font-bold text-white mb-6 max-md:mb-3 leading-tight mobile-compact-text"
            style={{
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              fontFeatureSettings: '"liga", "kern"',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              letterSpacing: '-0.02em',
            }}
          >
            Premium Ceramic Tint
            <br />
            <span
              className="gradient-text-hd bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-400 bg-clip-text text-transparent"
              style={{
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundImage:
                  'linear-gradient(135deg, #60a5fa 0%, #67e8f9 50%, #5eead4 100%)',
                filter: 'brightness(1.2) contrast(1.1)',
              }}
            >
              Installation in Columbia
            </span>
          </h1>

          <p
            className="text-xl md:text-2xl max-md:text-base text-gray-100 mb-8 max-md:mb-4 max-w-3xl mx-auto leading-relaxed mobile-compact-text"
            style={{
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
              letterSpacing: '0.01em',
            }}
          >
            Professional window tinting services in Columbia with lifetime
            warranty. Protect your investment with superior UV protection and
            heat rejection.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 max-md:gap-2 justify-center items-center mb-12 max-md:mb-6 mobile-compact-gap">
            <Link to="/contact">
              <Button
                size="lg"
                className="min-w-[200px] max-md:min-w-[160px] max-md:text-sm max-md:py-2 shadow-2xl hover:shadow-primary/60 transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98] hover:brightness-110"
                style={{
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  WebkitFontSmoothing: 'antialiased',
                  textRendering: 'optimizeLegibility',
                }}
              >
                <i className="ri-calendar-line mr-2 text-xl max-md:text-base"></i>
                Request Appointment
              </Button>
            </Link>

            <Button
              size="lg"
              variant="outline"
              className="min-w-[200px] max-md:min-w-[160px] max-md:text-sm max-md:py-2 bg-white/15 backdrop-blur-lg border-white/80 text-white hover:bg-white hover:text-secondary shadow-2xl hover:shadow-white/40 transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98] hover:brightness-110"
              style={{
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
                WebkitFontSmoothing: 'antialiased',
                textRendering: 'optimizeLegibility',
              }}
              onClick={() => (window.location.href = 'tel:4438000348')}
            >
              <i className="ri-phone-line mr-2 text-xl max-md:text-base"></i>
              (443) 800-0348
            </Button>
          </div>

          <div
            className="flex flex-wrap justify-center gap-8 max-md:gap-4 text-white mobile-compact-gap"
            style={{
              WebkitFontSmoothing: 'antialiased',
              textRendering: 'optimizeLegibility',
            }}
          >
            {/* Lifetime Warranty */}
            <div
              className="flex items-center space-x-2 max-md:space-x-1 transition-all duration-200 hover:scale-105"
              style={{
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
              }}
            >
              <i className="ri-shield-check-line text-accent text-2xl max-md:text-lg w-6 h-6 max-md:w-5 max-md:h-5 flex items-center justify-center"></i>
              <span
                className="font-medium whitespace-nowrap text-lg max-md:text-sm"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  letterSpacing: '0.005em',
                }}
              >
                Lifetime Warranty
              </span>
            </div>

            {/* 5‑Star Rated */}
            <div
              className="flex items-center space-x-2 max-md:space-x-1 transition-all duration-200 hover:scale-105"
              style={{
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
              }}
            >
              <i className="ri-star-fill text-accent text-2xl max-md:text-lg w-6 h-6 max-md:w-5 max-md:h-5 flex items-center justify-center"></i>
              <span
                className="font-medium whitespace-nowrap text-lg max-md:text-sm"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  letterSpacing: '0.005em',
                }}
              >
                5‑Star Rated
              </span>
            </div>

            {/* Same Day Service */}
            <div
              className="flex items-center space-x-2 max-md:space-x-1 transition-all duration-200 hover:scale-105"
              style={{
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
              }}
            >
              <i className="ri-time-line text-accent text-2xl max-md:text-lg w-6 h-6 max-md:w-5 max-md:h-5 flex items-center justify-center"></i>
              <span
                className="font-medium whitespace-nowrap text-lg max-md:text-sm"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  letterSpacing: '0.005em',
                }}
              >
                Same Day Service
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom statistics */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-r from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-md border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 max-md:py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-md:gap-4">
            <div className="text-center">
              <div className="text-white mb-2 max-md:mb-1">
                <AnimatedCounter end={2745} duration={2500} />
              </div>
              <div className="text-gray-300 text-sm md:text-base max-md:text-xs font-medium uppercase tracking-wider">
                Happy Clients
              </div>
            </div>

            <div className="text-center">
              <div className="text-white mb-2 max-md:mb-1">
                <AnimatedCounter end={7} duration={2200} suffix="+" />
              </div>
              <div className="text-gray-300 text-sm md:text-base max-md:text-xs font-medium uppercase tracking-wider">
                Years Experience
              </div>
            </div>

            <div className="text-center">
              <div className="text-white mb-2 max-md:mb-1">
                <AnimatedCounter end={100} duration={2300} suffix="%" />
              </div>
              <div className="text-gray-300 text-sm md:text-base max-md:text-xs font-medium uppercase tracking-wider">
                Satisfaction
              </div>
            </div>

            <div className="text-center">
              <div className="text-white mb-2 max-md:mb-1">
                <AnimatedCounter end={1000} duration={1800} isWarranty={true} />
              </div>
              <div className="text-gray-300 text-sm md:text-base max-md:text-xs font-medium uppercase tracking-wider">
                Warranty
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-40 max-md:bottom-28 left-1/2 transform -translate-x-1/2 animate-bounce-smooth"
        style={{
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
        }}
      >
        <i className="ri-arrow-down-line text-white text-3xl max-md:text-2xl w-8 h-8 max-md:w-6 max-md:h-6 flex items-center justify-center drop-shadow-lg"></i>
      </div>
    </section>
  );
}
